// Editable QC checklist for a case. The whole list is saved wholesale (PUT /checklist). Reflects
// the case's current checklist and lets editors toggle done, rename, add, and remove rows.
import { useEffect, useState } from 'react';
import { type Case, type ChecklistItem } from '@alignova/shared';
import { api } from '../../lib/api.ts';
import { useCan } from '../../auth/useCan.ts';
import { t } from '../../i18n/index.ts';
import { Button } from '../../ui/primitives.tsx';

export function CaseChecklist({ caseValue, onSaved }: { caseValue: Case; onSaved: () => void }) {
  const can = useCan();
  const editable = can('case:update');
  const [items, setItems] = useState<ChecklistItem[]>(caseValue.checklist);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Re-sync when the case reloads (e.g. another editor saved).
  useEffect(() => setItems(caseValue.checklist), [caseValue.checklist]);

  const toggle = (i: number) =>
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, done: !it.done } : it)));
  const remove = (i: number) => setItems((prev) => prev.filter((_, idx) => idx !== i));
  const add = () => {
    const label = draft.trim();
    if (!label) return;
    setItems((prev) => [...prev, { label, done: false }]);
    setDraft('');
  };

  async function save() {
    setError(null);
    setSaving(true);
    try {
      await api.put(`/cases/${caseValue.id}/checklist`, { items });
      onSaved();
    } catch {
      setError(t('cases.checklist.error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      {items.length === 0 ? (
        <p className="mb-3 text-sm text-muted">{t('cases.checklist.empty')}</p>
      ) : (
        <ul className="mb-3 space-y-2">
          {items.map((it, i) => (
            <li key={i} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={it.done}
                disabled={!editable}
                onChange={() => toggle(i)}
                className="h-4 w-4"
              />
              <span className={`flex-1 text-sm ${it.done ? 'text-muted line-through' : 'text-ink'}`}>
                {it.label}
              </span>
              {editable && (
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="text-xs text-status-urgent-fg"
                  aria-label="Remove"
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {editable && (
        <>
          <div className="mb-3 flex gap-2">
            <input
              className="flex-1 rounded border border-line bg-surface px-3 py-2 text-sm"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  add();
                }
              }}
              placeholder={t('cases.checklist.placeholder')}
            />
            <Button type="button" variant="ghost" onClick={add}>
              {t('cases.checklist.add')}
            </Button>
          </div>
          {error && <p className="mb-2 text-xs text-status-urgent-fg">{error}</p>}
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving}>
              {saving ? t('cases.checklist.saving') : t('cases.checklist.save')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
